import React, { useState, useCallback, useRef } from 'react';
import styles from './Window.module.css';
import { MACOS_MENUBAR_HEIGHT_PX } from '../../../constants';

interface WindowProps {
  /** Logical width of the desktop (to constrain dragging) */
  containerW: number;
  /** Logical height of the desktop (to constrain dragging) */
  containerH: number;
  /** CSS transform scale currently applied to the desktop — used to convert mouse delta to logical px */
  previewScale: number;
}

export function FinderWindow({ containerW, containerH, previewScale }: WindowProps) {
  const [pos, setPos] = useState({ x: Math.floor(containerW * 0.14), y: 60 });

  const dragState = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);

  const onTitleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };

    const onMove = (mv: MouseEvent) => {
      if (!dragState.current) return;
      const dx = (mv.clientX - dragState.current.startX) / previewScale;
      const dy = (mv.clientY - dragState.current.startY) / previewScale;
      const newX = dragState.current.origX + dx;
      // The top 75 px of the window (title bar chrome) can slide behind the
      // menu bar freely. Collision is detected at the 76th pixel downward.
      const newY = Math.max(MACOS_MENUBAR_HEIGHT_PX - 37, dragState.current.origY + dy);
      setPos({ x: Math.round(newX), y: Math.round(newY) });
    };

    const onUp = () => {
      dragState.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [pos, previewScale]);

  return (
    <>
      {/* Retina screenshot - set width only to preserve aspect ratio, browser handles retina automatically */}
      <img 
        src="/assets/window-finder.png" 
        alt="Finder window"
        className={styles.window}
        style={{ 
          left: pos.x, 
          top: pos.y,
          width: '1032px',
        }}
        onMouseDown={onTitleMouseDown}
        draggable={false}
      />
    </>
  );
}

/* ───────────────────────────────────────────────────────────────────────────
 * NOTE: Original HTML/CSS implementation preserved below (commented out)
 * Replaced with screenshot for pixel-perfect rendering
 * ─────────────────────────────────────────────────────────────────────────── */

/*
const SIDEBAR_ITEMS: { section: string; items: { label: string; icon: React.ReactNode }[] }[] = [
  { section: 'Favourites', items: [
    { label: 'AirDrop',      icon: <SbAirdropIcon /> },
    { label: 'Desktop',      icon: <SbDesktopIcon /> },
    { label: 'Documents',    icon: <SbDocumentIcon /> },
    { label: 'Downloads',    icon: <SbDownloadsIcon /> },
    { label: 'Applications', icon: <SbAppsIcon /> },
  ]},
  { section: 'Locations', items: [
    { label: 'iCloud Drive',  icon: <SbCloudIcon /> },
    { label: 'Macintosh HD',  icon: <SbDriveIcon /> },
  ]},
];

const FINDER_FILES = [
  { name: 'Desktop' },
  { name: 'Documents' },
  { name: 'Downloads' },
  { name: 'Movies' },
  { name: 'Music' },
  { name: 'Pictures' },
  { name: 'Applications' },
  { name: 'Library' },
];
*/

/* ─── Sidebar icon set (unused) ──────────────────────────────── */

/*
const SB_PROPS = { width: 13, height: 13, viewBox: '0 0 13 13', fill: 'none' } as const;

function SbAirdropIcon() {
  return (
    <svg {...SB_PROPS} stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <circle cx="6.5" cy="8" r="2" />
      <path d="M3 5.5a5 5 0 0 1 7 0" />
      <path d="M1 3.5a8 8 0 0 1 11 0" />
    </svg>
  );
}

function SbDesktopIcon() {
  return (
    <svg {...SB_PROPS} stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <rect x="1" y="1.5" width="11" height="7.5" rx="1.2" />
      <path d="M4 12h5M6.5 9v3" />
    </svg>
  );
}

function SbDocumentIcon() {
  return (
    <svg {...SB_PROPS} stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 1h5l2.5 2.5V12H3V1z" />
      <path d="M8 1v2.5h2.5" />
      <line x1="4.5" y1="6" x2="8.5" y2="6" />
      <line x1="4.5" y1="8" x2="8.5" y2="8" />
      <line x1="4.5" y1="10" x2="7" y2="10" />
    </svg>
  );
}

function SbDownloadsIcon() {
  return (
    <svg {...SB_PROPS} stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <path d="M6.5 1v7M4 6l2.5 2.5L9 6" />
      <path d="M2 10v1.5h9V10" />
    </svg>
  );
}

function SbAppsIcon() {
  return (
    <svg {...SB_PROPS} fill="currentColor">
      <rect x="1" y="1" width="4.5" height="4.5" rx="1" />
      <rect x="7.5" y="1" width="4.5" height="4.5" rx="1" />
      <rect x="1" y="7.5" width="4.5" height="4.5" rx="1" />
      <rect x="7.5" y="7.5" width="4.5" height="4.5" rx="1" />
    </svg>
  );
}

function SbCloudIcon() {
  return (
    <svg {...SB_PROPS} stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <path d="M3.5 9.5a2.5 2.5 0 0 1 0-5 3.5 3.5 0 0 1 6.8.8A2 2 0 0 1 10 9.5H3.5z" />
    </svg>
  );
}

function SbDriveIcon() {
  return (
    <svg {...SB_PROPS} stroke="currentColor" strokeWidth="1.2" strokeLinecap="round">
      <rect x="1" y="3" width="11" height="7" rx="1.5" />
      <circle cx="9.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
      <line x1="2.5" y1="6.5" x2="7" y2="6.5" />
    </svg>
  );
}
*/
